import { useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import type { CurrentUser } from '@/react/lib/capabilities';

import { selectVisibleNav } from './selectVisibleNav';
import type { NavFeature } from './types';
import { SidebarHeader } from './SidebarHeader';
import { SidebarSection } from './SidebarSection';
import {
  SidebarUserProfile,
  type SidebarLogout,
  type SidebarUser,
} from './SidebarUserProfile';
import { useSidebarCollapse } from './useSidebarCollapse';

const NO_FEATURES: readonly NavFeature[] = [];

export interface SidebarProps {
  // The signed-in user's capabilities, for UX gating (server policies remain
  // authoritative). Absent when signed out → fail closed.
  currentUser?: CurrentUser;
  // Feature flags that are on, for flag-gated nav entries. Absent → none.
  enabledFeatures?: readonly NavFeature[];
  user: SidebarUser;
  accountPath: string;
  oauthAppsPath?: string;
  logout: SidebarLogout;
  // Override for tests/storybook; defaults to the live browser location.
  pathname?: string;
}

function resolvePathname(pathnameProp?: string) {
  return pathnameProp ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
}

// The sidebar mount is `data-turbo-permanent`, so it survives Turbo visits
// without remounting (no flash). That means it can't rely on a one-time read of
// window.location for active state — re-read it on every Turbo navigation
// (and back/forward). When pathname is passed explicitly
// (tests/storybook) the location stays controlled and these listeners are off.
function useReactivePathname(pathnameProp?: string) {
  const controlled = pathnameProp !== undefined;
  const [autoPathname, setAutoPathname] = useState(() => resolvePathname());

  useEffect(() => {
    if (controlled) return;
    const update = () => setAutoPathname(window.location.pathname);
    // Optimistic active state: flip as soon as a Turbo visit starts, from the
    // visit's target URL — not after the response lands. turbo:load then
    // corrects the rare divergence (a redirect to a different path).
    const onVisit = (event: Event) => {
      const url = (event as CustomEvent<{ url?: string }>).detail?.url;
      if (!url) return;
      const target = new URL(url, window.location.origin);
      setAutoPathname(target.pathname);
    };
    update();
    document.addEventListener('turbo:visit', onVisit);
    document.addEventListener('turbo:load', update);
    // A visit whose fetch fails (offline, dropped connection) never renders, so
    // turbo:load never fires — without this the optimistic highlight would stick
    // to a page the user never reached. Error *responses* (4xx/5xx) do render, so
    // turbo:load covers those.
    document.addEventListener('turbo:fetch-request-error', update);
    window.addEventListener('popstate', update);
    return () => {
      document.removeEventListener('turbo:visit', onVisit);
      document.removeEventListener('turbo:load', update);
      document.removeEventListener('turbo:fetch-request-error', update);
      window.removeEventListener('popstate', update);
    };
  }, [controlled]);

  // Controlled (tests/storybook) reads the prop each render so it stays
  // reactive; uncontrolled (the app) tracks the live URL via turbo:load.
  return controlled ? resolvePathname(pathnameProp) : autoPathname;
}

// The app sidebar, fully in React. Owns collapse state; derives active/expanded
// state from the URL; gates nav entries by the user's capabilities. Mounted as
// a React island from dashboard_layout.rb.
export function Sidebar({
  currentUser,
  enabledFeatures = NO_FEATURES,
  user,
  accountPath,
  oauthAppsPath,
  logout,
  pathname: pathnameProp,
}: SidebarProps) {
  const { collapsed, toggle } = useSidebarCollapse();
  const pathname = useReactivePathname(pathnameProp);
  const sections = useMemo(() => selectVisibleNav(currentUser, enabledFeatures), [currentUser, enabledFeatures]);

  return (
    <aside
      data-sidebar-collapsed={collapsed || undefined}
      className={cn(
        'font-inter h-screen bg-off-white border-r border-disable flex flex-col overflow-hidden transition-[width] duration-300 ease-in-out',
        collapsed ? 'w-24' : 'w-60',
      )}
    >
      <SidebarHeader collapsed={collapsed} onToggle={toggle} />

      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
        {sections.map((section, index) => (
          <SidebarSection
            key={section.heading ?? `group-${index}`}
            section={section}
            isFirst={index === 0}
            collapsed={collapsed}
            pathname={pathname}
          />
        ))}
      </div>

      <SidebarUserProfile user={user} accountPath={accountPath} oauthAppsPath={oauthAppsPath} logout={logout} collapsed={collapsed} />
    </aside>
  );
}

export default Sidebar;
